/* React */
import React, { useState } from "react";

/* Utils */
import SwiperWrapper from "src/utils/wrapper/swiper-wrapper/SwiperWrapper";
import { getCohortGroupColor } from "src/utils/helper/charts/colorMapper";
import { useEffectiveShowNoValueEntries } from "src/utils/hooks/useEffectiveShowNoValueEntries";

/* Components */
import StatsSlide from "src/components/charts/vertical-boxplot/stats-container/stats-slide/StatsSlide";

/* Stylesheet */
import styles from "./StatsContainer.module.scss";

/* Constants */
import {
  excludeCohortNoValueGroups,
  orderCohortGroups,
} from "src/shared/constants/charts/NoValueStates";

/* Interfaces */
import { BoxplotProps } from "src/shared/interfaces/charts/Boxplot";

interface StatsContainerProps {
  data: BoxplotProps[];
}

const StatsContainer: React.FC<StatsContainerProps> = ({
  data,
}) => {
  const [activeIndex, setActiveIndex] = useState<number>(0);

  // Gated by custom-cohort state so the stats never surface no-value groups or
  // counts the request (and the page/legend) treat as off - see the hook's docs.
  const showNoValueEntries = useEffectiveShowNoValueEntries();

  const chartData = orderCohortGroups(
    excludeCohortNoValueGroups(data, showNoValueEntries)
  );

  // Hiding no-value groups can shrink `chartData` below the active swiper
  // index; clamp it so the color lookup doesn't fall through to a fallback
  // palette color for a now-missing slide.
  const safeActiveIndex = Math.min(
    activeIndex,
    Math.max(chartData.length - 1, 0)
  );
  const consistentColor = getCohortGroupColor(
    chartData[safeActiveIndex]?.name,
    safeActiveIndex
  );

  return (
    <div
      style={{ height: chartData.length > 1 ? "92.5%" : "100%" }}
      className={styles.stats_container}
    >
      <SwiperWrapper
        scssProps={{
          backgroundColor: "transparent",
        }}
        data={chartData}
        handleActiveIndex={(index) => setActiveIndex(index)}
        renderItem={(item) => {
          const noValueStats: Record<string, number> = {};
          if (showNoValueEntries) {
            if ((item.notLookedForCount ?? 0) > 0) {
              noValueStats.notLookedForCount =
                item.notLookedForCount!;
            }
            if ((item.lookedForNoValueCount ?? 0) > 0) {
              noValueStats.lookedForNoValueCount =
                item.lookedForNoValueCount!;
            }
          }
          const displayStats = {
            ...item.stats,
            ...noValueStats,
          };
          return (
            <StatsSlide
              stats={displayStats}
              color={consistentColor}
            />
          );
        }}
      />
    </div>
  );
};

export default StatsContainer;
